/* ══════════════════════════════════════════════════════════════
   src/content/character-creation/sheetText.ts
   Text printed on the official character sheets, guides and the
   Additional Sheets (creation page, sheet sidecar, companion sheet),
   copied from the PDFs. Used by the character builder and sheet.
   ══════════════════════════════════════════════════════════════ */

/** Named areas of the character sheet and guide; the builder highlights them step by step. */
export type SheetSection =
  | 'class' | 'identity' | 'evasion' | 'traits' | 'damage' | 'hope' | 'experience' | 'gold'
  | 'classFeature' | 'weapons' | 'armor' | 'inventory' | 'inventoryWeapons'
  | 'description' | 'background' | 'connections' | 'heritage' | 'domainCards' | 'level';

/** How each area is named on its edit button ("Edit traits"). */
export const SECTION_NAMES: Record<SheetSection, string> = {
  class: 'class', identity: 'name and pronouns', evasion: 'armor', traits: 'traits', damage: 'armor', hope: 'Hope',
  experience: 'Experiences', gold: 'gold', classFeature: 'class', weapons: 'weapons', armor: 'armor', inventory: 'inventory',
  inventoryWeapons: 'inventory', description: 'description', background: 'background', connections: 'connections',
  heritage: 'heritage', domainCards: 'domain cards', level: 'level',
};

export type SheetPageName = 'sheet' | 'guide' | 'cards';

export interface CreationStep {
  title: string;
  text: string[];
  page: SheetPageName;
  sections: SheetSection[];
}

/** The nine steps of the Character Creation page, in order. */
export const CREATION_STEPS: CreationStep[] = [
  {
    title: 'Choose a Class',
    text: [
      "Your class represents the kind of character you'll be playing and gives you access to certain abilities and spells during the game. Take the character sheet specific to this class.",
      "Each class has a number of subclasses to choose from that help define what kind of focus you'd like your character to have. Take the foundation card for your chosen subclass.",
    ],
    page: 'sheet',
    sections: ['class', 'identity', 'classFeature'],
  },
  {
    title: 'Choose a Heritage',
    text: [
      "Your character's heritage is made up of both an ancestry and a community card. These two cards establish your character's physical appearance and the environment they grew up in.",
    ],
    page: 'cards',
    sections: ['heritage'],
  },
  {
    title: 'Assign Character Traits',
    text: [
      "Distribute the values -1, 0, 0, +1, +1, +2 across your character traits. When rolling, you'll add the value of that character trait to the roll.",
    ],
    page: 'sheet',
    sections: ['traits'],
  },
  {
    title: 'Record Character Information',
    text: ['Next, record your starting Evasion. Additionally, gain 2 Hope to start the game.'],
    page: 'sheet',
    sections: ['evasion', 'damage', 'hope'],
  },
  {
    title: 'Choose Starting Equipment',
    text: [
      'Decide which weapons you want to start the game with and record their details in the appropriate spaces in the "Active Weapons" section.',
      'Next, take one of the available options for starting armor and record its details in the appropriate spaces. Your armor comes with its own damage thresholds and base armor score. Record this alongside your selection under "Active Armor." Add your thresholds to your level and record those numbers under the "Damage & Health" section. Also record the Base Armor Score, plus modifiers from any features, in the large shield labeled "Armor" at the top of the sheet.',
      'The starting inventory for your class is listed on your character guide. Record those items in the "Inventory" section of your character sheet.',
    ],
    page: 'sheet',
    sections: ['weapons', 'armor', 'inventory', 'gold'],
  },
  {
    title: 'Create Your Background',
    text: [
      "Make description choices from the available options on your character guide (or create your own). This will help you start to build out your character's look and attitude.",
      "Then use the background questions on your character guide as a jumping-off point to build out your character's history (or create your own questions).",
      'Finally, name your character and choose which pronouns they use.',
    ],
    page: 'guide',
    sections: ['description', 'background'],
  },
  {
    title: 'Create Your Experiences',
    text: [
      "Use all the choices and backstory you've made about your character so far to generate their starting two Experiences, a set of narrative words or phrases that represent what they've learned or become on their journey so far. Assign both a modifier of +2.",
    ],
    page: 'sheet',
    sections: ['experience'],
  },
  {
    title: 'Choose Domain Deck Cards',
    text: [
      "Each class is made up of two different domains, which are listed below the class name on the character sheet. Choose two cards total from the level 1 cards in those domains. You can share these decks with other players. If you do, make sure to talk with them about which cards they're interested in.",
    ],
    page: 'cards',
    sections: ['domainCards'],
  },
  {
    title: 'Create Connections',
    text: [
      "Ask any of the Connection questions on your character guide to the other players at the table (or generate your own questions). Use this as an opportunity to also discuss how your characters all met, what brought them together, and why they've decided to travel as a party.",
    ],
    page: 'guide',
    sections: ['connections'],
  },
];

/** The Character Sheet Sidecar: what each area of the sheet is for. */
export const SIDECAR: Partial<Record<SheetSection, string>> = {
  class: 'This section denotes your class. Each class is made up of two domains. You choose your abilities and spells from these domains.',
  identity: 'This section gives you a place to record your name, pronouns, heritage (the combination of your community and ancestry), and subclass. There is also a space to record your level.',
  evasion: 'Evasion represents your ability to avoid an attack. The GM rolls against this number when determining if an adversary hits you. If you do get hit, you can choose to mark an Armor Slot (the small empty shields to the right of the large "Armor" shield) to reduce the severity of the incoming damage by one threshold. You have a number of available slots equal to your Armor Score (the number in the large "Armor" shield).',
  traits: 'This area contains your six character traits. You use these as your primary modifiers on action rolls. The descriptions beneath each trait remind you how to use them.',
  damage: "The two smaller boxes are your damage thresholds. These come from your armor, and you always add your level to their value. When you take damage, compare it to the numbers here. If the incoming damage is Minor, you mark a Hit Point. If it is Major, you mark 2 Hit Points. If it is Severe, you mark 3 Hit Points. If you ever need to mark Stress and can't, mark a Hit Point. If you ever mark your last Hit Point, you must make a death move.",
  hope: "This is where you record your Hope. You spend Hope to use an Experience (details below), Help an Ally, use a Hope Feature, or initiate a Tag Team Roll. When you Help an Ally, you explain how you're aiding them and roll a d6 advantage die they add to their action roll total. You start with 2 Hope marked. Your class's unique Hope Feature is in this section as well.",
  experience: "Experience represents your character's particular specializations your character has. These are narrative tags that you create to reflect what your character is skilled in. When they apply to the scene, you can spend a Hope before the action roll to add an Experience's value as a modifier. You earn more Experiences throughout a campaign.",
  gold: 'This is where you keep track of your gold during a campaign. If you would mark your tenth handful, you instead mark a bag and erase all your handfuls. If you would mark your tenth bag, you instead mark a chest and erase your bags. You start with one handful of gold.',
  classFeature: 'Each class starts with a unique class feature that sets them apart from other classes. Make sure to read through them before you begin playing.',
  weapons: "This is the section for your active weapons. Along with a name, weapons have an associated trait that you roll when using them for an attack. They also have a range (Melee, Very Close, Close, Far, or Very Far). The GM tells you what range a target is at relative to your character. Damage dice are the kind of dice you roll when you make a successful attack, and the damage type is either physical or magic. Proficiency tells you how many damage dice you roll. A weapon's feature describes its special abilities or the requirements to use it. Weapons are either one-handed or two-handed. Fill in the appropriate number of hands based on the weapons you take.",
  armor: "This is the section for your armor. Along with a name, armor has base thresholds that contribute to your damage thresholds, and a base score that contributes to your Armor Score. The armor's feature describes its special abilities or the requirements to use it.",
  inventory: 'Your inventory is where you store items and consumables.',
  inventoryWeapons: "This is where you store weapons you want to carry but not have active. While a weapon is in your inventory, you can't benefit from its feature.",
};

export const FIRST_NAMES = [
  'Alucard', 'Ambrose', 'Ash', 'Bellamy', 'Calder', 'Calypso', 'Chartreuse', 'Clover', 'Dahlia', 'Darrow',
  'Deacon', 'Elowen', 'Emrys', 'Fable', 'Fiorella', 'Flynn', 'Gatlin', 'Gerard', 'Hadron', 'Harlow', 'Indigo',
  'Isla', 'Jaden', 'Kai', 'Kismet', 'Leo', 'Mika', 'Moon', 'Nyx', 'Orna', 'Phaedra', 'Quill', 'Rani', 'Raphael',
  'Reza', 'Roux', 'Saffron', 'Sierra', 'Skye', 'Talon', 'Thea', 'Triton', 'Vala', 'Velo', 'Wisteria', 'Yanelle', 'Zahara',
];

export const FAMILY_NAMES = [
  'Abbot', 'Advani', 'Agoston', 'Baptiste', 'Belgarde', 'Blossom', 'Chance', 'Covault', 'Dawn', 'Dennison',
  'Drayer', 'Emrick', 'Foley', 'Fury', 'Grove', 'Hartley', 'Humfleet', 'Hyland', 'Ikeda', 'Jones', 'Jordon', 'Kaan',
  'Knoth', 'Lagrange', 'Lockamy', 'Lyon', 'Marche', 'Merrell', 'Newland', 'Novak', 'Orwick', 'Overholt', 'Pray',
  'Rathbone', 'Rose', 'Seagrave', 'Spurlock', 'Thorn', 'Tringle', 'Vasquez', 'Warren', 'Worth', 'York',
];

export const REGION_NAMES = [
  'Alvyon', 'Bloomfare', 'Bonecross', 'Branishar', 'Cloud Isles', 'Davesh Pass', 'End of Journeys', 'Fearhold',
  "Ir'thandir", 'Mountains of Creation', 'Nocturne', 'Revenance', 'Shattered Peaks', 'South Choir',
  "Sunbearer's Crescent", "Watcher's Ravine", 'Wilting Valley', 'Xuria',
];

export const PLACE_NAMES = [
  'Balking Tide', 'Black Willow', "Crow's Nest", 'Dire Reach', 'Figero', "Fool's Gold", 'Golden Goose',
  'Hearthbreak', 'Hollow Keep', 'Idle Fiend', 'Kross', 'Limping Liar', "Mettler's Make", "Nero's Compass",
  'Netherwell', 'Parting Gift', 'Quiet Magnitude', 'Roaming Way', 'Salute', 'Wicked Smile',
];

export const EXPERIENCE_EXAMPLES: Array<{ label: string; items: string[] }> = [
  { label: 'Backgrounds like', items: ['Bodyguard', 'Con Artist', 'Merchant', 'Noble', 'Pirate'] },
  { label: 'Characteristics like', items: ['Affable', 'Intimidating', 'Sticky Fingers', 'Survivor'] },
  { label: 'Specialties like', items: ['Healer', 'Inventor', 'Navigator', 'Swashbuckler'] },
  { label: 'Skills like', items: ['Barter', 'Quick Hands', 'Repair', 'Tracker'] },
  { label: 'Phrases like', items: ['Catch Me If You Can', 'Hold the Line', "Nature's Friend", 'This Is Not a Negotiation'] },
];

export interface LevelUpOption {
  boxes: number;
  /** Wide boxes mark the options that use both of a tier's choices. */
  wide?: boolean;
  text: string;
}

export interface LevelUpTier {
  tier: number;
  levels: string;
  intro: string;
  choose: string;
  options: LevelUpOption[];
}

const BASE_OPTIONS = (cardLimit: string): LevelUpOption[] => [
  { boxes: 3, text: 'Gain a +1 bonus to two unmarked character traits and mark them.' },
  { boxes: 2, text: 'Permanently gain one Hit Point slot.' },
  { boxes: 2, text: 'Permanently gain one Stress slot.' },
  { boxes: 1, text: 'Permanently gain a +1 bonus to two Experiences.' },
  { boxes: 1, text: `Choose an additional domain card of your level or lower from a domain you have access to${cardLimit}.` },
  { boxes: 1, text: 'Permanently gain a +1 bonus to your Evasion.' },
];

const UPPER_OPTIONS: LevelUpOption[] = [
  { boxes: 1, text: 'Take an upgraded subclass card. Then cross out the multiclass option for this tier.' },
  { boxes: 2, wide: true, text: 'Increase your Proficiency by +1.' },
  { boxes: 2, wide: true, text: 'Multiclass: Choose an additional class for your character, then cross out an unused "Take an upgraded subclass card" and the other multiclass option on this sheet.' },
];

/** The three level-up columns at the bottom of every character guide. Line breaks follow the printed guide. */
export const LEVEL_UP_TIERS: LevelUpTier[] = [
  {
    tier: 2, levels: 'Levels 2-4',
    intro: 'At level 2, gain an additional\nExperience at +2 and gain a\n+1 bonus to your Proficiency.',
    choose: 'Choose two options from the list below and\nmark them.',
    options: BASE_OPTIONS(' (up to level 4)'),
  },
  {
    tier: 3, levels: 'Levels 5-7',
    intro: 'At level 5, gain an additional\nExperience at +2 and clear all marks\non character traits. Then gain a\n+1 bonus to your Proficiency.',
    choose: 'Choose two options from the list below or any from\nthe previous tier and mark them.',
    options: [...BASE_OPTIONS(' (up to level 7)'), ...UPPER_OPTIONS],
  },
  {
    tier: 4, levels: 'Levels 8-10',
    intro: 'At level 8, gain an additional\nExperience at +2 and clear all marks\non character traits. Then gain a\n+1 bonus to your Proficiency.',
    choose: 'Choose two options from the list below or any from\nthe previous tier and mark them.',
    options: [...BASE_OPTIONS(''), ...UPPER_OPTIONS],
  },
];

export const LEVEL_UP_FOOTER =
  'Update your level and adjust your damage\nthresholds accordingly. Take an additional domain\ncard of your level or lower from a domain you\nhave access to.';

/** The Ranger Companion sheet (Beastbound). */
export const COMPANION_SHEET = {
  intro: 'Work with the GM to decide what kind of animal you have as your companion. Give them a name and draw or attach a picture of them in the space above. Then create two Experiences for your companion based on their training and the history you have together. Finally, describe their method of dealing damage (their standard attack) and record it in the "Attack & Damage" section. Their damage starts at d6 and their range starts at Melee.',
  experience: 'Start with +2 in two Experiences. Whenever you gain a new Experience, your companion also gains one. All new Experiences start at +2.',
  examples: [
    'Bold Distraction', 'Expert Climber', 'Fetch', 'Friendly', 'Guardian of the Forest', 'Horrifying', 'Intimidating',
    'Loyal Until the End', 'Navigation', 'Nimble', 'Nobody Left Behind', 'On High Alert', 'Protective',
    'Royal Companion', 'Scout', 'Service Animal', 'Trusted Mount', 'Vigilant', 'We Always Find Them',
    "You Can't Hit What You Can't Find",
  ],
  command: 'Make a Spellcast Roll to connect with your companion and command them to take action. Spend a Hope to add an applicable Companion Experience to the roll. On a success with Hope, if your next action builds on their success, you gain advantage on the roll.',
  attack: 'When you command your companion to attack, they gain any benefits that would normally only apply to you (such as the effects of "Ranger\'s Focus"). On a success, their damage roll uses your Proficiency and their damage die.',
  stress: [
    'When your companion would take any amount of damage, they mark a Stress. When they mark their last Stress, they drop out of the scene (by hiding, fleeing, or a similar action). They remain unavailable until the start of your next long rest, where they return with 1 Stress cleared.',
    'When you choose a downtime move that clears Stress on yourself, your companion clears an equal number of Stress.',
  ],
  training: 'When your character levels up, choose one available option for your companion from the following list and mark it here.',
  options: [
    { boxes: 3, name: 'Intelligent', text: 'Your companion gains a permanent +1 bonus to a Companion Experience of your choice.' },
    { boxes: 1, name: 'Light in the Dark', text: 'Use this as an additional Hope slot your character can mark.' },
    { boxes: 1, name: 'Creature Comfort', text: 'Once per rest, when you take time during a quiet moment to give your companion love and attention, you can gain a Hope or you can both clear a Stress.' },
    { boxes: 1, name: 'Armored', text: 'When your companion takes damage, you can mark one of your Armor Slots instead of marking one of their Stress.' },
    { boxes: 3, name: 'Vicious', text: "Increase your companion's damage dice or range by one step (d6 to d8, Close to Far, etc.)." },
    { boxes: 3, name: 'Resilient', text: 'Your companion gains an additional Stress slot.' },
    { boxes: 1, name: 'Bonded', text: 'When you mark your last Hit Point, your companion rushes to your side to comfort you. Roll a number of d6s equal to the unmarked Stress slots they have and mark them. If any roll a 6, your companion helps you up. Clear your last Hit Point and return to the scene.' },
    { boxes: 3, name: 'Aware', text: 'Your companion gains a permanent +2 bonus to their Evasion.' },
  ],
};
